import { useMutation, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import type { ITourIntegration } from 'src/services/api/properties/types';

export function useTourIntegrationMutations(propertyId: number) {
    const queryClient = useQueryClient();
    const invalidate = () => queryClient.invalidateQueries(queryKeys.properties);

    const create = useMutation<
        ITourIntegration,
        Error,
        { provider: string; externalId: string | null }
    >(
        async ({ provider, externalId }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/tour-integrations`;
            return apiClient(path, {
                method: 'post',
                json: {
                    provider,
                    external_property_id: externalId,
                },
            }).json<ITourIntegration>();
        },
        { onSuccess: invalidate }
    );

    const update = useMutation<
        ITourIntegration,
        Error,
        { integrationId: number; externalId: string | null }
    >(
        async ({ integrationId, externalId }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/tour-integrations/${integrationId}`;
            return apiClient(path, {
                method: 'patch',
                json: {
                    external_property_id: externalId,
                },
            }).json<ITourIntegration>();
        },
        { onSuccess: invalidate }
    );

    const remove = useMutation<void, Error, { integrationId: number }>(
        async ({ integrationId }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/tour-integrations/${integrationId}`;
            await apiClient(path, { method: 'delete' });
        },
        { onSuccess: invalidate }
    );

    return { create, update, remove };
}
